import { Suspense, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { Center, Html, OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import RotateLeftRoundedIcon from "@mui/icons-material/RotateLeftRounded";

const MODEL_URLS = {
  BSU: encodeURI("/models/BSU-3N-AS01 - BASE SUPPRESSION UNIT.glb"),
  MSU: encodeURI("/models/MSU-AS01 - MOBILE SUPPRESSION UNIT.glb"),
};

const DEFAULT_MODEL_URL = encodeURI(
  "/models/CFPS-CFRP-120VAC-1N-AS02 - CONTROL PANEL.glb",
);

function modelUrlForSystemType(systemType) {
  return MODEL_URLS[systemType] || DEFAULT_MODEL_URL;
}

const CAMERA_BY_TYPE = {
  BSU: { mobile: [3, 2, 3], desktop: [6, 3.5, 6] },
  MSU: { mobile: [-1.45, 0.26, 2.41], desktop: [-1.45, 0.26, 2.41] },
};

const DEFAULT_CAMERA = {
  mobile: [2.2, 0.35, 2.42],
  desktop: [2.2, 0.35, 2.42],
};

const DOOR_PART_MATCHERS = [
  (name) => name.startsWith("MODEL-305-DR"),
  (name) => name.includes("DOOR-GASKET"),
  (name) => name.startsWith("LATCH"),
  (name) => name.includes("PC MONITOR"),
  (name) => name.includes("NS RACK"),
];

const DOOR_HINGE_PIVOT = new THREE.Vector3(-0.455, 1.32, 0.206);
const DOOR_SWING_AXIS = new THREE.Vector3(0, 1, 0);
const DOOR_OPEN_ANGLE = THREE.MathUtils.degToRad(-100);

function isDoorPart(name) {
  const normalized = name.replace(/_/g, " ");
  return DOOR_PART_MATCHERS.some((match) => match(normalized));
}

function openControlPanelDoor(root) {
  root.updateMatrixWorld(true);

  const swing = new THREE.Quaternion().setFromAxisAngle(
    DOOR_SWING_AXIS,
    DOOR_OPEN_ANGLE,
  );
  const hingeTransform = new THREE.Matrix4()
    .makeTranslation(DOOR_HINGE_PIVOT.x, DOOR_HINGE_PIVOT.y, DOOR_HINGE_PIVOT.z)
    .multiply(new THREE.Matrix4().makeRotationFromQuaternion(swing))
    .multiply(
      new THREE.Matrix4().makeTranslation(
        -DOOR_HINGE_PIVOT.x,
        -DOOR_HINGE_PIVOT.y,
        -DOOR_HINGE_PIVOT.z,
      ),
    );

  const parts = [];
  root.traverse((obj) => {
    if (!isDoorPart(obj.name)) return;
    for (let ancestor = obj.parent; ancestor; ancestor = ancestor.parent) {
      if (isDoorPart(ancestor.name)) return;
    }
    parts.push(obj);
  });

  const parentInverse = new THREE.Matrix4();
  for (const part of parts) {
    const newWorld = hingeTransform.clone().multiply(part.matrixWorld);
    parentInverse.copy(part.parent.matrixWorld).invert();
    parentInverse.multiply(newWorld).decompose(
      part.position,
      part.quaternion,
      part.scale,
    );
  }
}

function ProductModel({ url, openDoor = false }) {
  const { scene } = useGLTF(url);
  const preparedScene = useMemo(() => {
    const root = scene.clone(true);
    if (openDoor) openControlPanelDoor(root);
    return root;
  }, [scene, openDoor]);
  return <primitive object={preparedScene} />;
}

function ModelLoadingFallback() {
  return (
    <Html center>
      <Stack alignItems="center" spacing={1} sx={{ pointerEvents: "none" }}>
        <CircularProgress size={28} />
        <Typography variant="caption" color="text.secondary">
          Loading 3D model…
        </Typography>
      </Stack>
    </Html>
  );
}

export default function SystemViewport({ systemType }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const cameraSet = CAMERA_BY_TYPE[systemType] ?? DEFAULT_CAMERA;
  const cameraPosition = isMobile ? cameraSet.mobile : cameraSet.desktop;
  const modelUrl = modelUrlForSystemType(systemType);

  const [isRotating, setIsRotating] = useState(false);
  const controlsRef = useRef(null);

  const handleReset = () => {
    controlsRef.current?.reset();
    setIsRotating(false);
  };

  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "action.hover",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        height: { xs: 280, md: 400 },
        overflow: "hidden",
      }}
    >
      <Button
        size="small"
        variant="contained"
        color="inherit"
        startIcon={isRotating ? <PauseRoundedIcon /> : <RotateLeftRoundedIcon />}
        onClick={() => setIsRotating((r) => !r)}
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 1,
          bgcolor: "background.paper",
          boxShadow: 1,
        }}
      >
        {isRotating ? "Pause" : "Rotate"}
      </Button>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        startIcon={<CenterFocusStrongRoundedIcon />}
        onClick={handleReset}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: "background.paper",
          boxShadow: 1,
        }}
      >
        Reset view
      </Button>
      <Canvas
        camera={{ position: cameraPosition, fov: 50 }}
        dpr={[1, 2]}
        style={{ width: "100%", height: "100%" }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 10, 5]} intensity={0.8} />
        <Suspense fallback={<ModelLoadingFallback />}>
          <Center>
            <ProductModel
              url={modelUrl}
              openDoor={modelUrl === DEFAULT_MODEL_URL}
            />
          </Center>
        </Suspense>
        <OrbitControls
          ref={controlsRef}
          makeDefault
          enablePan={false}
          minDistance={2}
          maxDistance={12}
          autoRotate={isRotating}
          autoRotateSpeed={1.5}
        />
      </Canvas>
    </Box>
  );
}
